import { Suspense, useCallback, useRef } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { SingleDateCalendar } from '@xemplo/calendar/v2';
import { DateTimeFormat } from '@xemplo/date-time';
import { DawnCalendar } from '@xemplo/icons';
import { V2, V2Types } from '@xemplo/popover';

import { createDateChangeEvent } from '../date-field.helper';
import { DateValue } from '../date-field.types';

import * as S from './date-selector.styles';
import { DateSelectorProps } from './date-selector.types';

export function DateSelector({
  inputSize,
  disabled,
  readOnly,
  onValueChange,
  onChange,
  dateFormat = DateTimeFormat.Default,
  ...calendarProps
}: DateSelectorProps) {
  const popoverRef = useRef<V2Types.PopoverRef>(null);

  const handleSelect = useCallback(
    (value?: DateValue) => {
      onValueChange?.(value);
      const formattedValue = value ? dayjs(value).format(dateFormat) : '';
      onChange?.(createDateChangeEvent(formattedValue));
      popoverRef.current?.setOpen(false);
    },
    [onValueChange, onChange, dateFormat],
  );

  return (
    <V2.Popover.Root
      ref={popoverRef}
      placement="bottom-end"
      disabled={disabled || readOnly}
    >
      <V2.Popover.Trigger>
        {({ open }) => (
          <S.TriggerElementWrapper
            id="date-field-selector-trigger"
            data-testid="date-field-selector-trigger"
            className={classNames(inputSize, { open, disabled, readOnly })}
          >
            <DawnCalendar fontSize={24} />
          </S.TriggerElementWrapper>
        )}
      </V2.Popover.Trigger>
      <V2.Popover.Popup>
        <Suspense fallback={null}>
          <SingleDateCalendar {...calendarProps} onSelect={handleSelect} />
        </Suspense>
      </V2.Popover.Popup>
    </V2.Popover.Root>
  );
}
